// ---------------------------------------------------------------------------
// Guidance disclosure — a dismissible wrapper for "how this page works" blocks.
//
// Measured 2026-07-30 at 1440x900, signed-out empty workspace:
//   /studio/agent   4 concept cards (15.4%) + 3-step strip (12.7%) = 28.1% of
//                   the first screen, zero interactive elements inside.
//   /studio/import  the Add source -> Shape -> Save -> Share strip is 112 px
//                   (9.9%) on desktop and 382 px (39.7%) at 390 px wide.
// Both were permanently expanded: a returning creator re-read the tutorial
// every visit and paid for it in first-screen space.
//
// This folds them instead of deleting them — the block is always one keypress
// away, and the choice is remembered. Same discipline as
// `openpencil-source-panel-preferences.ts`: a storage refusal is reported, not
// swallowed, because a control that silently forgets is worse than one that
// never claimed to remember.
// ---------------------------------------------------------------------------
import { ChevronDown } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";
import { useStudioLocale } from "@/i18n";

const KEY_PREFIX = "studio.guidance.";

export function guidanceDisclosureKey(id: string): string {
  return `${KEY_PREFIX}${id}`;
}

/** `null` = never chose, which is deliberately distinct from "chose open". */
export function readGuidanceCollapsed(id: string): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(guidanceDisclosureKey(id));
    if (raw === "true") return true;
    if (raw === "false") return false;
    return null;
  } catch {
    return null;
  }
}

export function writeGuidanceCollapsed(
  id: string,
  collapsed: boolean,
): "stored" | "rejected" {
  if (typeof window === "undefined") return "rejected";
  try {
    window.localStorage.setItem(
      guidanceDisclosureKey(id),
      collapsed ? "true" : "false",
    );
    return "stored";
  } catch {
    return "rejected";
  }
}

export function GuidanceDisclosure({
  id,
  title,
  summary,
  children,
  className,
  testId,
}: {
  /** Stable across releases — it is the storage key. */
  id: string;
  title: string;
  /** One line that survives collapsing, so the block stays identifiable. */
  summary: string;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  // Progressive disclosure (2026-08-04): the default is COLLAPSED, so a page
  // opens on its task and the tutorial is one keypress away rather than the
  // other way round. Measured cost of the old default: 28.1% of the first
  // screen at 1440x900 (54.9% at 390px) spent on blocks with no interactive
  // element. The `summary` line keeps the block identifiable while folded, so
  // nothing is hidden without a trace, and an explicit choice still wins over
  // this default in both directions.
  const [collapsed, setCollapsed] = useState(
    () => readGuidanceCollapsed(id) ?? true,
  );
  const [rejected, setRejected] = useState(false);
  const { strings } = useStudioLocale();
  const bodyId = `guidance-${id}`;
  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      setRejected(writeGuidanceCollapsed(id, next) === "rejected");
      return next;
    });
  }, [id]);

  return (
    <section
      className={`rounded-2xl border border-border bg-card ${className ?? ""}`}
      data-testid={testId ?? `guidance-${id}`}
      data-collapsed={String(collapsed)}
    >
      <h2 className="flex">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          data-testid={`guidance-${id}-toggle`}
          title={
            collapsed ? strings.a11y.showSection(title) : strings.a11y.hideSection(title)
          }
          className="flex min-w-0 flex-1 items-center gap-2 rounded-2xl px-4 py-3 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
              collapsed ? "-rotate-90" : ""
            }`}
            aria-hidden
          />
          <span className="flex min-w-0 flex-1 flex-col sm:flex-row sm:items-baseline sm:gap-2">
            <span className="whitespace-nowrap text-sm font-semibold text-foreground">
              {title}
            </span>
            {collapsed ? (
              <span
                className="min-w-0 truncate text-xs font-normal text-muted-foreground"
                data-testid={`guidance-${id}-summary`}
              >
                {summary}
              </span>
            ) : null}
          </span>
        </button>
      </h2>
      <div id={bodyId} className={collapsed ? "hidden" : "px-4 pb-4"} hidden={collapsed}>
        {children}
      </div>
      {rejected ? (
        <p
          className="px-4 pb-3 text-xs text-muted-foreground"
          role="status"
          data-testid={`guidance-${id}-persistence-warning`}
        >
          This browser would not store the setting, so this will be folded
          again next time.
        </p>
      ) : null}
    </section>
  );
}
